// components/tier-branch/TransferUserDialog.tsx
// Shift selected user(s) from their current branch to another branch.
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Box, Typography, CircularProgress,
} from "@mui/material";
import * as api from "../../services/tierBranchApi";
import type { Branch, Tier } from "../../types/tierBranch";
import type { Notify } from "./useSnack";

const TransferUserDialog: React.FC<{
  open: boolean;
  userIds: number[];
  fromBranchId: number | null;
  branches: Branch[];
  onClose: () => void;
  onDone: () => void;
  notify: Notify;
}> = ({ open, userIds, fromBranchId, branches, onClose, onDone, notify }) => {
  const [toBranchId, setToBranchId] = useState<number | "">("");
  const [toTierId, setToTierId] = useState<number | "">("");
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setToBranchId("");
      setToTierId("");
      setReason("");
      api.listTiers().then(setTiers).catch(() => setTiers([]));
    }
  }, [open]);

  const targets = branches.filter((b) => b.id !== fromBranchId);

  const handleTransfer = async () => {
    if (toBranchId === "" && toTierId === "") {
      notify("Select a target tier and/or branch", "warning");
      return;
    }
    setSaving(true);
    try {
      // assignUser sets tier and/or branch in one call (manual move + audit).
      await api.assignUser({
        userIds,
        ...(toBranchId !== "" ? { branchId: Number(toBranchId) } : {}),
        ...(toTierId !== "" ? { tierId: Number(toTierId) } : {}),
        reason: reason || undefined,
      });
      notify(`Moved ${userIds.length} user(s)`, "success");
      onDone();
      onClose();
    } catch (e: any) {
      notify(e?.message || "Transfer failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Move Users — Tier / Branch</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Moving {userIds.length} user(s).
          </Typography>
          <TextField
            select
            label="Target Tier (optional)"
            size="small"
            fullWidth
            value={toTierId}
            onChange={(e) => setToTierId(e.target.value === "" ? "" : Number(e.target.value))}
          >
            <MenuItem value="">— leave tier unchanged —</MenuItem>
            {tiers.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Target Branch (optional)"
            size="small"
            fullWidth
            value={toBranchId}
            onChange={(e) => setToBranchId(e.target.value === "" ? "" : Number(e.target.value))}
          >
            <MenuItem value="">— leave branch unchanged —</MenuItem>
            {targets.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason (optional)"
            size="small"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleTransfer}
          disabled={saving || userIds.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferUserDialog;
